<script lang="ts">
	import { t } from '$lib/translations'
	import { X } from 'lucide-svelte'
	import { hvacBrands } from '$lib/consts'
	import { getAdvancedFilterModalState, getMarketplaceDataState } from '$lib/stores'
	import { BROWSER } from 'esm-env'
	import { page } from '$app/stores'
	import { Tabs, ToggleGroup } from 'bits-ui'
	import { setCookie } from '$lib/utils'

	const advancedFilterModalState = getAdvancedFilterModalState()
	const marketplaceDataState = getMarketplaceDataState()

	$: brands = Object.values(hvacBrands).filter((brand) =>
		brand.countries.includes($page.data.country)
	)

	const brandImages: Record<string, string> = {
		'alpha-innotec': '/images/brands/marketplace/alpha-innotec.jpg',
		buderus: '/images/brands/marketplace/buderus.svg',
		bosch: '/images/brands/marketplace/bosch.svg',
		daikin: '/images/brands/marketplace/daikin.svg',
		'de-dietrich': '/images/brands/marketplace/de-dietrich.svg',
		grundig: '/images/brands/marketplace/grundig.svg',
		hsd: '/images/brands/marketplace/hsd.png',
		kronoterm: '/images/brands/marketplace/kronoterm.svg',
		lg: '/images/brands/marketplace/lg.svg',
		maxa: '/images/brands/marketplace/maxa.svg',
		nibe: '/images/brands/marketplace/nibe.svg',
		regulus: '/images/brands/marketplace/regulus.svg',
		samsung: '/images/brands/marketplace/samsung.svg',
		stiebel: '/images/brands/marketplace/stiebel.svg',
		templari: '/images/brands/marketplace/templari.svg',
		vaillant: '/images/brands/marketplace/vaillant.svg',
		viessmann: '/images/brands/marketplace/viessmann.svg',
		wltr: '/images/brands/marketplace/wltr.svg'
	}

	$: advancedFilterModalState.subscribe((state) => {
		if (BROWSER) {
			if (state.open === true) {
				const scrollX = document.querySelector('html')?.scrollTop || 0
				window.onscroll = () => window.scrollTo(0, scrollX)
			} else {
				window.onscroll = (event) => event
			}
		}
	})

	let noiseLevel: string

	$: $advancedFilterModalState?.open, (noiseLevel = $marketplaceDataState.noiseLevel || '')

	function setGlobalData(data: Record<string, string | number | boolean | null | undefined>) {
		marketplaceDataState.update((state) => ({
			...state,
			...data
		}))
		setCookie('MARKETPLACE_FILTER_DATA', JSON.stringify(data), 30)
	}

	let activeTab = 0
	$: $advancedFilterModalState?.open, (activeTab = $advancedFilterModalState.showTab)
</script>

{#if $advancedFilterModalState?.open}
	<div
		class="hide-scrollbar animate-opacity fixed left-0 top-0 z-[10000] flex h-full w-full items-start justify-center overflow-y-scroll bg-white/20 backdrop-blur-sm"
	>
		<div
			class="absolute h-full w-full"
			role="none"
			on:click={() => {
				advancedFilterModalState.update((state) => ({
					...state,
					open: false,
					showTab: 0
				}))
			}}
		/>

		<div
			class="animate-in relative mt-2 flex min-h-screen w-full max-w-[780px] flex-col overflow-hidden bg-white pt-8 shadow-lg sm:mx-2 sm:my-8 sm:mt-20 sm:min-h-[500px] sm:rounded-3xl"
		>
			<h2 class="unstyled px-4 text-left text-2xl font-medium sm:px-8">
				{t('web.marketplace.advancedFilter')}
			</h2>

			<Tabs.Root value={activeTab.toString()}>
				<Tabs.List
					class="mx-3 my-6 grid grid-cols-2 gap-1 rounded-lg p-1 text-w-blue-950 sm:mx-8 sm:bg-neutral-200/70"
				>
					<Tabs.Trigger
						value="0"
						class="col-span-1 w-full rounded-md border border-neutral-200 bg-transparent bg-white py-2 active:bg-w-red-300 data-[state=active]:border-w-blue-950 data-[state=active]:bg-w-blue-950 data-[state=active]:text-white sm:border-none sm:bg-transparent sm:data-[state=active]:bg-white sm:data-[state=active]:text-w-blue-950"
						>{t('web.forms.brand')}</Tabs.Trigger
					>
					<Tabs.Trigger
						value="1"
						class="col-span-1 w-full rounded-md border border-neutral-200 bg-transparent bg-white py-2 active:bg-w-red-300 data-[state=active]:border-w-blue-950 data-[state=active]:bg-w-blue-950 data-[state=active]:text-white sm:border-none sm:bg-transparent sm:data-[state=active]:bg-white sm:data-[state=active]:text-w-blue-950"
						>{t('web.forms.params')}</Tabs.Trigger
					>
				</Tabs.List>
				<Tabs.Content value="0">
					<h3 class="unstyled mt-4 px-4 text-left text-lg font-medium sm:px-8">
						{t('web.marketplace.filter.brands')}
					</h3>
					<div
						class="mt-4 grid auto-rows-fr grid-cols-2 gap-3 px-4 xs:grid-cols-3 sm:grid-cols-4 sm:px-8"
					>
						{#each brands as brand}
							<button
								on:click={() => {
									marketplaceDataState.update((state) => ({
										...state,
										supplierTitle:
											$marketplaceDataState.supplierTitle === brand.slug ? '' : brand.slug
									}))
									setGlobalData($marketplaceDataState)
								}}
								class="inline-block rounded-lg border-2 p-4 align-top transition-colors {$marketplaceDataState.supplierTitle ===
								brand.slug
									? 'border-w-red-500 hover:border-w-red-500'
									: 'border-neutral-200 hover:border-neutral-500'}"
							>
								<span class="flex h-auto max-h-12">
									<img
										src={brandImages[brand.slug]}
										alt="{brand.name} logo"
										class="object-contain"
									/>
								</span>
							</button>
						{/each}
					</div>

					{#if $marketplaceDataState.supplierTitle}
						<div class="mx-4 mt-4 rounded-xl bg-neutral-100 sm:mx-8 sm:mt-8">
							{#each brands as brand}
								{#if $marketplaceDataState.supplierTitle === brand.slug}
									<div class="px-5 py-4">
										<h4 class="unstyled font-semibold">{brand.name}</h4>
										<p class="unstyled mt-2 text-sm opacity-70">
											{t(`web.marketplace.brand.${brand.slug}`)}
										</p>
									</div>
								{/if}
							{/each}
						</div>
					{/if}
				</Tabs.Content>
				<Tabs.Content value="1">
					<h3 class="unstyled mt-4 px-4 text-left text-lg font-medium sm:px-8">
						{t('web.marketplace.filter.noiseLevel')}
					</h3>
					<div class="mt-3 px-4 sm:px-8">
						<ToggleGroup.Root
							class="flex w-fit flex-wrap gap-2 rounded-lg"
							bind:value={noiseLevel}
							onValueChange={(value) => {
								marketplaceDataState.update((state) => ({
									...state,
									noiseLevel: value === '' ? null : value
								}))
								setGlobalData($marketplaceDataState)
							}}
						>
							<ToggleGroup.Item
								class="max-w-fit flex-1 text-nowrap rounded-lg bg-neutral-100 px-4 py-2 text-sm font-medium transition-colors hover:bg-neutral-200 data-[state=on]:bg-w-red-500 data-[state=on]:text-white"
								value="">{t('web.marketplace.filter.noLimit')}</ToggleGroup.Item
							><ToggleGroup.Item
								class="max-w-fit flex-1 text-nowrap rounded-lg bg-neutral-100 px-4 py-2 text-sm font-medium transition-colors hover:bg-neutral-200 data-[state=on]:bg-w-red-500 data-[state=on]:text-white"
								value="60">{t('web.marketplace.filter.upto')} 60 db</ToggleGroup.Item
							>

							<ToggleGroup.Item
								class="max-w-fit flex-1 text-nowrap rounded-lg bg-neutral-100 px-4 py-2 text-sm font-medium transition-colors hover:bg-neutral-200 data-[state=on]:bg-w-red-500 data-[state=on]:text-white"
								value="50">{t('web.marketplace.filter.upto')} 50 db</ToggleGroup.Item
							>
							<ToggleGroup.Item
								class="max-w-fit flex-1 text-nowrap rounded-lg bg-neutral-100 px-4 py-2 text-sm font-medium transition-colors hover:bg-neutral-200 data-[state=on]:bg-w-red-500 data-[state=on]:text-white"
								value="40">{t('web.marketplace.filter.upto')} 40 db</ToggleGroup.Item
							>
						</ToggleGroup.Root>
					</div>
				</Tabs.Content>
			</Tabs.Root>

			<div class="mt-auto p-4 sm:p-8">
				<button
					class="btn-new btn-new-blue w-full"
					on:click={() => {
						advancedFilterModalState.update((state) => ({
							...state,
							open: false,
							showTab: 0
						}))
					}}>{t('web.marketplace.filter.hvac.showFiltered')}</button
				>
			</div>
			<button
				class="absolute right-0 top-0 rounded-bl-3xl px-4 py-3.5"
				on:click={() => {
					advancedFilterModalState.update((state) => ({
						...state,
						open: false,
						showTab: 0
					}))
				}}
				><X color="#061c3e" class="hidden sm:block" size={16} />
				<X color="#061c3e" class="sm:hidden" size={24} /></button
			>
		</div>
	</div>
	<!-- <section class=" leave-contact fixed left-0 top-0 z-[10000] h-full w-full backdrop-blur-sm">
		<div
			class="fixed top-20 h-full w-full bg-neutral-100 opacity-80"
			on:keypress
			on:click={() => {
				advancedFilter = false
			}}
			on:keydown={(e) => {
				if (e.key == 'Enter') advancedFilter = false
			}}
			role="button"
			tabindex="0"
		/>
		<div class="h-screen overflow-y-auto pb-[5%]">
			<div
				class="w-max-full wrapper relative z-10 mx-auto mt-20 flex w-[800px] max-w-full flex-col items-end justify-center rounded-xl bg-white pb-4 shadow-md"
			>
				<span
					class="close h-8 w-8 p-2"
					on:click={() => {
						advancedFilterModalState.update((state) => ({
							...state,
							open: false
						}))
					}}
					on:keydown={(e) => {
						if (e.key == 'Enter') advancedFilter = false
					}}
					role="button"
					tabindex="0"
					><svg
						class="h-full w-full"
						viewBox="0 0 11 11"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M10.4587 1.5405L9.45991 0.541748L5.50033 4.50133L1.54074 0.541748L0.541992 1.5405L4.50158 5.50008L0.541992 9.45967L1.54074 10.4584L5.50033 6.49883L9.45991 10.4584L10.4587 9.45967L6.49908 5.50008L10.4587 1.5405Z"
							fill="black"
						/>
					</svg></span
				>
				<div class="w-full px-4">
					<h2 class="unstyled text-xl font-semibold">
						{t('web.marketplace.advancedFilter')}
					</h2>
					<div class="mb-4 mt-8 flex items-center justify-between">
						<h3 class="unstyled font-semibold">
							{t('web.marketplace.filter.brands')}
						</h3>
						<div class="flex">
							<button on:click={() => scrollElement(scrollContainer, false)} class="p-2"
								><ChevronLeft /></button
							>
							<button on:click={() => scrollElement(scrollContainer)} class="p-2"
								><ChevronRight /></button
							>
						</div>
					</div>

					<div
						bind:this={scrollContainer}
						class="no-scrollbar w-full overflow-x-auto whitespace-nowrap"
					>
						{#each brands as item}
							{#if !countrySpecific || item.countries.includes(countrySpecific)}
								<button
									on:click={() => {
										console.log('brandClicked')
									}}
									class="mr-2 inline-block rounded-md border p-4 align-top {item.slug !==
									displayBrand
										? 'border-neutral-300'
										: 'border-primary-50'}  h-20 w-36"
								>
									<span class="flex h-auto max-h-12">
										<img src={brandImages[item.slug]} alt="" />
									</span>
								</button>
							{/if}
						{/each}
					</div>
					<div class="mt-4 rounded-xl bg-neutral-100">
						{#each brands as item}
							{#if displayBrand == item.slug}
								<div class="p-4">
									<h4 class="unstyled mb-2 font-semibold">{item.name}</h4>
									<p class="text-sm">
										{t(`web.marketplace.brand.${item.slug}`)}
									</p>
								</div>
							{/if}
						{/each}
					</div>
					<h3 class="unstyled mt-8 font-semibold">
						{t('web.marketplace.filter.deviceParameters')}
					</h3>
					<!-- <Select
                    flex
                    {translation}
                    name="refrigerantType"
                    placeholder={t('web.marketplace.filter.any')}
                    label={t('web.marketplace.filter.coolingType')}
                >
                    <option value="">{t('web.marketplace.filter.any')}</option>
                    <option value="R290">R290</option>
                    <option value="R32">R32</option>
                    <option value="R404A">R404A</option>
                    <option value="R407C">R407C</option>
                    <option value="R410A">R410A</option>
                </Select> -->
	<!-- <Select
		flex
		translation={t}
		placeholder={t('web.marketplace.filter.noLimit')}
		name="noiseLevel"
		label={t('web.marketplace.filter.noiseLevel')}
	>
		<option value="50">{t('web.marketplace.filter.upto')} 50dB</option>
		<option value="40">{t('web.marketplace.filter.upto')} 40dB</option>
		<option value="50">{t('web.marketplace.filter.upto')} 50dB</option>
		<option value="60">{t('web.marketplace.filter.upto')} 60dB</option>
	</Select> -->
	<!-- <Select
                    flex
                    {translation}
                    placeholder={t('web.marketplace.filter.anyAlt')}
                    name="colors"
                    label={t('web.marketplace.filter.color')}
                >
                    <option value="">{t('web.marketplace.filter.anyAlt')}</option>
                    <option value="3938">{t('web.marketplace.filter.color.white')}</option>
                    <option value="1852">{t('web.marketplace.filter.color.black')}</option>
                    <option value="3939">{t('web.marketplace.filter.color.black')}</option>
                    <option value="3940">{t('web.marketplace.filter.color.other')}</option>
                </Select> -->
	<!-- <Select
                    flex
                    {translation}
                    placeholder={t('web.marketplace.filter.anyAlt')}
                    name="width"
                    label="Velikost"
                >
                    <option value="">{t('web.marketplace.filter.anyAlt')}</option>
                    <option value="60">Max 60cm šířky</option>
                    <option value="80">Max 80cm šířky</option>
                    <option value="100">Max 100cm šířky</option>
                    <option value="120">Max 120cm šířky</option>
                </Select> -->
	<!-- <h3 class="unstyled text-base font-semibold mt-8 my-4">
                    {t('web.marketplace.filter.options')}
                </h3> -->
	<!-- <Checkbox border name="inStock" value="true"
                    >{t('web.marketplace.filter.onlyInStock')}</Checkbox
                > -->
	<!--<div
						on:click={() => {
							advancedFilter = false
							// hideMobileFilter = true
						}}
						on:keydown={(e) => {
							if (e.key == 'Enter') {
								advancedFilter = false
								// hideMobileFilter = true
							}
						}}
						role="link"
						tabindex="0"
						class="mt-8 w-full rounded-md bg-primary-500 p-4 text-center text-white"
					>
						{t('web.marketplace.filter.hvac.showFiltered')}
					</div>
				</div>
			</div>
		</div>
	</section> -->
{/if}

<style>
	.animate-in {
		animation: slide-in 0.25s ease-out both;
	}

	.animate-opacity {
		animation: opacity-in 0.25s ease-out both;
	}

	@keyframes slide-in {
		0% {
			transform: translateY(-20%) scale(0.5);
			opacity: 0;
		}
		100% {
			transform: translateY(0) scale(1);
			opacity: 1;
		}
	}

	@keyframes opacity-in {
		0% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}
</style>
